function tokenKey(kind, text) {
  return `${kind}\0${text.toLocaleLowerCase()}`;
}

function addToken(tokens, seen, text, kind) {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  if (!trimmed) {
    return;
  }

  const key = tokenKey(kind, trimmed);
  if (seen.has(key)) {
    return;
  }

  seen.add(key);
  tokens.push({ text: trimmed, kind });
}

export function collectCatalogTokens(features = []) {
  const tokens = [];
  const seen = new Set();
  for (const feature of features) {
    if (!feature) {
      continue;
    }

    addToken(tokens, seen, feature.name, 'feature');
    addToken(tokens, seen, feature.displayText, 'feature');
    if (typeof feature.name === 'string' && feature.name.includes('.')) {
      addToken(tokens, seen, feature.name.split('.').pop(), 'feature');
    }

    for (const value of feature.values || []) {
      if (typeof value === 'string') {
        addToken(tokens, seen, value, 'value');
        continue;
      }

      addToken(tokens, seen, value?.value, 'value');
      addToken(tokens, seen, value?.displayText, 'value');
    }
  }

  return tokens.sort((left, right) => {
    if (left.kind !== right.kind) {
      return left.kind === 'feature' ? -1 : 1;
    }

    return right.text.length - left.text.length;
  });
}

function isTokenChar(character) {
  return /[0-9A-Za-z._-]/.test(character);
}

function isQuote(character) {
  return character === "'" || character === '\u2019' || character === '\u2032';
}

const mentionStopwords = new Set([
  'a', 'an', 'the', 'to', 'for', 'of', 'on', 'in', 'is', 'are', 'be', 'or', 'and',
  'as', 'at', 'by', 'from', 'with', 'this', 'that', 'it', 'its', 'i', 'you', 'we',
  'they', 'he', 'she', 'me', 'my', 'your', 'do', 'did', 'does', 'if', 'then', 'so',
  'but', 'not', 'no', 'yes', 'up', 'look', 'please', 'can', 'why', 'how'
]);

function isStopword(token) {
  return mentionStopwords.has((token || '').toLowerCase());
}

function matchesAt(text, index, token) {
  const atMention = text[index] === '@';
  const tokenStart = atMention ? index + 1 : index;

  if (tokenStart + token.length > text.length) {
    return 0;
  }

  if (text.substr(tokenStart, token.length).localeCompare(token, undefined, { sensitivity: 'accent' }) !== 0) {
    return 0;
  }

  const matched = text.substr(tokenStart, token.length);
  const allCaps = /[A-Z]/.test(matched) && matched === matched.toUpperCase();
  if (isStopword(token) && !atMention && !allCaps) {
    return 0;
  }

  const beforeIndex = atMention ? index : tokenStart;
  const beforeOk = beforeIndex === 0 || !isTokenChar(text[beforeIndex - 1]);
  const afterIndex = tokenStart + token.length;
  const afterOk = afterIndex >= text.length || !isTokenChar(text[afterIndex]);
  if (!beforeOk || !afterOk) {
    return 0;
  }

  const last = token[token.length - 1];
  if (isQuote(last) && tokenStart > 0 && isQuote(text[tokenStart - 1])) {
    return 0;
  }

  return afterIndex - index;
}

export function splitMentions(text, tokens = []) {
  if (!text) {
    return [];
  }

  const segments = [];
  let index = 0;
  while (index < text.length) {
    let consumed = 0;
    const match = tokens.find(token => {
      consumed = matchesAt(text, index, token.text);
      return consumed > 0;
    });
    if (match && consumed > 0) {
      const atMention = text[index] === '@';
      segments.push({
        type: 'mention',
        text: text.slice(atMention ? index + 1 : index, index + consumed),
        kind: match.kind
      });
      index += consumed;
      continue;
    }

    const start = index;
    index += 1;
    while (index < text.length && !tokens.some(token => matchesAt(text, index, token.text) > 0)) {
      index += 1;
    }
    segments.push({ type: 'text', text: text.slice(start, index) });
  }

  return segments;
}

export function mentionQuery(text, cursor) {
  const before = text.slice(0, cursor ?? text.length);
  const match = before.match(/@([A-Za-z0-9._-]*)$/);
  if (!match) {
    return null;
  }

  return { start: before.length - match[0].length, query: match[1] };
}

export function filterMentionTokens(tokens, query, limit = 8) {
  const needle = (query || '').toLowerCase();
  return tokens
    .filter(token => !needle || token.text.toLowerCase().startsWith(needle))
    .slice(0, limit);
}
